import { useState, type KeyboardEvent, type SyntheticEvent } from 'react';
import { Download, MessageSquarePlus, MoreHorizontal } from 'lucide-react';
import { IconButton, Menu, MenuItem } from '@strivelabs/ui-core/v2';
import { cn } from '@strivelabs/ui-core/lib/utils';

interface FileActionsMenuProps {
  filename: string;
  /** Opens a new chat with the file attached. */
  onAsk: () => void;
  onDownload: () => void;
  /** Classes for the trigger, for a tile that shows it only on hover. */
  triggerClassName?: string;
}

/**
 * What can be done to one file besides opening it (which its row or tile
 * does on click), behind a "more" button: ask about it in a new chat, or
 * download it.
 *
 * Sits inside a row or a tile that opens the file on click, so its own clicks
 * and keys stop here: the menu's panel is portalled, but React still bubbles
 * its events up through this component to the row.
 */
export function FileActionsMenu({
  filename, onAsk, onDownload, triggerClassName,
}: FileActionsMenuProps) {
  const [ open, setOpen ] = useState(false);
  const ariaLabel = `Actions for ${filename}`;
  const stop = (event: SyntheticEvent) => event.stopPropagation();
  // Only the keys a row opens on: Escape and the arrows have to reach the
  // menu's own listeners on the document.
  const stopActivation = (event: KeyboardEvent<HTMLSpanElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.stopPropagation();
    }
  };

  return (
    <span className="inline-flex" onClick={stop} onKeyDown={stopActivation}>
      <Menu
        open={open}
        onOpenChange={setOpen}
        align="end"
        aria-label={ariaLabel}
        contentClassName="w-[188px]"
        trigger={(
          <IconButton
            size="xs"
            aria-label={ariaLabel}
            aria-haspopup="menu"
            aria-expanded={open}
            className={cn(triggerClassName, open && 'opacity-100')}
          >
            <MoreHorizontal size={16} />
          </IconButton>
        )}
      >
        <MenuItem label="Ask about this file" onClick={onAsk}>
          <MessageSquarePlus size={14} className="shrink-0" />
          Ask about this file
        </MenuItem>
        <MenuItem label="Download" onClick={onDownload}>
          <Download size={14} className="shrink-0" />
          Download
        </MenuItem>
      </Menu>
    </span>
  );
}
